/* Eden V12 — folder-feed intro, reversible hero exit, one-time entrances.
   Scroll work reads the real scroll container: Horizon scrolls `.page-wrapper` at >=990px
   and `document.scrollingElement` below that, so window-only listeners never fire on desktop. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const squeeze = matchMedia('(min-width: 990px)');
  const option = (key, fallback) => {
    const n = parseFloat(getComputedStyle(document.body).getPropertyValue(`--eden-${key}`));
    return Number.isFinite(n) ? n : fallback;
  };
  const enabled = () => !reduced.matches && !window.Shopify?.designMode && document.body.dataset.edenMotion === 'true';
  /** The element Horizon actually scrolls, or null when the document scrolls. */
  const scroller = () => (squeeze.matches ? document.querySelector('.page-wrapper') : null);
  const clamp01 = (t) => Math.min(1, Math.max(0, t));

  /* ------------------------------------------------------------------ intro */

  const intro = document.getElementById('eden-intro');
  if (intro && !intro.hidden) {
    const wrapper = document.querySelector('.page-wrapper');
    const previousInert = wrapper?.inert;
    if (wrapper) wrapper.inert = true;

    const stage = intro.querySelector('.eden-intro-stage');
    const app = intro.querySelector('.eden-intro-app');
    const feed = intro.querySelector('.eden-intro-feed');
    const cards = Array.from(intro.querySelectorAll('.eden-post-card'));
    const posts = Array.from(intro.querySelectorAll('.eden-intro-post'));
    const fronts = Array.from(intro.querySelectorAll('.eden-folder-front'));
    const smear = intro.querySelector('#eden-motion-smear feGaussianBlur');
    const cursor = intro.querySelector('.eden-intro-cursor');
    const arrow = intro.querySelector('.eden-cursor-arrow');
    const showCursor = intro.dataset.cursor !== 'false';

    const start = Number(intro.dataset.started);
    const duration = Number(intro.dataset.duration) || 3300;
    // Two flings between three cards, then the last card opens to full screen.
    const flings = [duration * 0.17, duration * 0.42];
    const openAt = Math.max(duration * 0.55, duration - 1250);
    const leaveAt = Math.min(openAt + 640, duration - 260);

    /**
     * Geometry is read once, never per frame: the cards carry their own transform, which
     * makes each post its own offset parent and would otherwise report the same offsetTop
     * for all three. Layout does not change while the intro plays.
     */
    let centres = [];
    let middle = 0;
    let box = { x: 0, y: 0, w: 0, h: 0 };
    const centreFor = (i) => (centres[i] == null ? 0 : middle - centres[i]);

    let index = 0;
    let position = 0;
    let velocity = 0;
    let cursorX = 0, cursorY = 0, cursorVX = 0, cursorVY = 0;
    let driftVX = 0, driftVY = 0; // low-passed velocity: the tilt must not flicker
    let press = 0;                // 0 resting, 1 mid-flick
    let lastSmear = -1;
    let opened = false;
    let leaving = false;
    let frame = 0;
    let timer = 0;
    let ended = false;
    let previousTime = start;
    let measured = false;

    const measure = () => {
      const rect = app.getBoundingClientRect();
      const host = intro.getBoundingClientRect();
      box = { x: rect.left - host.left, y: rect.top - host.top, w: rect.width, h: rect.height };
      middle = app.clientHeight / 2;
      centres = cards.map((card, i) => posts[i].offsetTop + card.offsetTop + card.offsetHeight / 2);
      position = centreFor(index);
      cursorX = box.x + box.w * 0.62;
      cursorY = box.y + box.h * 1.25;
      measured = true;
    };

    const cleanup = () => {
      if (ended) return;
      ended = true;
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      if (wrapper) wrapper.inert = previousInert;
      document.removeEventListener('keydown', escape);
      reduced.removeEventListener('change', preference);
      document.removeEventListener('visibilitychange', visibility);
      if (intro.contains(document.activeElement)) {
        const main = document.getElementById('MainContent');
        const old = main?.getAttribute('tabindex');
        main?.setAttribute('tabindex', '-1');
        main?.focus({ preventScroll: true });
        if (main && old === null) main.removeAttribute('tabindex');
        else if (main) main.setAttribute('tabindex', old);
      }
    };
    const finish = () => window.edenIntroFinish?.();
    const escape = (event) => { if (event.key === 'Escape') finish(); };
    const preference = () => { if (reduced.matches) finish(); };
    const visibility = () => { if (document.hidden) finish(); };
    document.addEventListener('eden:intro:done', cleanup, { once: true });
    document.addEventListener('keydown', escape);
    reduced.addEventListener('change', preference);
    document.addEventListener('visibilitychange', visibility);
    // A rotation or a resize mid-intro would leave the cached geometry stale.
    addEventListener('resize', () => { if (!opened) measured = false; }, { passive: true });

    /** Where the pointer wants to be, in intro-local pixels. */
    const cursorTarget = (elapsed) => {
      if (elapsed < duration * 0.07) return [box.x + box.w * 0.62, box.y + box.h * 1.25];
      let target = [box.x + box.w * 0.6, box.y + box.h * 0.64];
      flings.forEach((at, i) => {
        // A flick: down-page grab, fast swipe up, then the hand drifts back.
        if (elapsed >= at - 120 && elapsed < at + 420) {
          const t = clamp01((elapsed - at + 120) / 540);
          const swipe = t < 0.25 ? t / 0.25 * 0.18 : 0.18 + (1 - Math.exp(-(t - 0.25) * 6)) * 0.62;
          target = [box.x + box.w * (0.6 - i * 0.06 + swipe * 0.08), box.y + box.h * (0.78 - swipe * 0.72)];
        }
      });
      return target;
    };

    const openCard = () => {
      if (opened) return;
      opened = true;
      // Zoom on the folder's body, not its bounding box: the tab must not leave a gap.
      const rect = (fronts[fronts.length - 1] ?? cards[cards.length - 1]).getBoundingClientRect();
      const host = stage.getBoundingClientRect();
      // Enough overscale that the body's rounded corners stay off screen.
      const scale = Math.max(innerWidth / rect.width, innerHeight / rect.height) * 1.32;
      const dx = innerWidth / 2 - (rect.left + rect.width / 2);
      const dy = innerHeight / 2 - (rect.top + rect.height / 2);
      stage.style.transformOrigin = `${rect.left + rect.width / 2 - host.left}px ${rect.top + rect.height / 2 - host.top}px`;
      feed.style.removeProperty('filter'); // nothing smears once the folder is opening
      stage.classList.add('is-opening');
      intro.classList.add('is-opening');
      // Keep zooming while the veil fades, so the card swallows the screen rather than
      // finishing early and sitting still.
      stage.style.transitionDuration = `${Math.round(leaveAt - openAt + 180)}ms`;
      // Next frame, so the origin and the transition are in place before the value changes.
      requestAnimationFrame(() => { stage.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`; });
      cursor.classList.remove('is-live');
    };

    const draw = (time) => {
      if (ended) return;
      if (!measured) measure();
      const elapsed = Math.max(0, time - start);
      const dt = Math.min(34, Math.max(1, time - previousTime));
      previousTime = time;

      if (!opened) {
        // Advance to the next card as each fling lands.
        const next = flings.filter((at) => elapsed >= at).length;
        if (next !== index && next < cards.length) {
          index = next;
          velocity -= 0.34; // the flick's own momentum, on top of the spring
        }
        const target = centreFor(index);
        const w = 0.011, z = 0.56; // slight overshoot, settles in about 600ms
        velocity += (-2 * z * w * velocity - w * w * (position - target)) * dt;
        position += velocity * dt;
        feed.style.transform = `translate3d(0, ${position.toFixed(2)}px, 0)`;

        /**
         * Folders hold their presence through the middle of the frame and then dissolve
         * quickly near the edges: a smoothstep, not a straight ramp, so nothing appears
         * to fade while it is still the one being looked at.
         */
        posts.forEach((post, i) => {
          const away = clamp01(Math.abs(position + centres[i] - middle) / (middle * 1.7));
          const curve = away * away * (3 - 2 * away);
          post.style.transform = `scale(${(1 - curve * 0.14).toFixed(4)}) translateZ(0)`;
          post.style.opacity = Math.max(0, 1 - curve * 1.08).toFixed(3);
        });

        /**
         * Motion smear. An SVG filter blurs on the Y axis only, so a sweep streaks along
         * its own travel instead of going soft in every direction. Quantised, and removed
         * outright once the feed settles, so a resting frame costs nothing to paint.
         */
        if (smear) {
          const reach = innerWidth < 750 ? 5 : 9;
          const wanted = Math.min(reach, Math.abs(velocity) * 7.5);
          const step = Math.round(wanted * 4) / 4;
          if (step !== lastSmear) {
            lastSmear = step;
            if (step < 0.3) feed.style.removeProperty('filter');
            else {
              smear.setAttribute('stdDeviation', `0 ${step}`);
              feed.style.filter = 'url(#eden-motion-smear)';
            }
          }
        }
      }

      if (showCursor && !opened) {
        if (elapsed > duration * 0.07) cursor.classList.add('is-live');
        const [tx, ty] = cursorTarget(elapsed);
        // Near-critical damping: it arrives without the wobble that reads as two objects.
        const cw = 0.019, cz = 0.92;
        cursorVX += (-2 * cz * cw * cursorVX - cw * cw * (cursorX - tx)) * dt;
        cursorVY += (-2 * cz * cw * cursorVY - cw * cw * (cursorY - ty)) * dt;
        cursorX += cursorVX * dt; cursorY += cursorVY * dt;
        cursor.style.transform = `translate3d(${cursorX.toFixed(2)}px, ${cursorY.toFixed(2)}px, 0)`;

        /**
         * The arrow leans into the swipe and presses down as the flick starts, both driven
         * by a smoothed velocity so the movement reads as one continuous gesture.
         */
        const ease = Math.min(1, dt / 130);
        driftVX += (cursorVX - driftVX) * ease;
        driftVY += (cursorVY - driftVY) * ease;
        const speed = Math.hypot(driftVX, driftVY);
        const wanted = Math.min(1, speed * 1.5);
        press += (wanted - press) * Math.min(1, dt / 120);
        const tilt = Math.max(-11, Math.min(11, driftVX * 26 - driftVY * 7));
        arrow.style.transform = `rotate(${tilt.toFixed(2)}deg) scale(${(1 - press * 0.16).toFixed(3)})`;
      }

      if (!opened && elapsed >= openAt) openCard();
      if (!leaving && elapsed >= leaveAt) {
        leaving = true;
        intro.classList.add('is-leaving');
        document.documentElement.classList.add('eden-intro-handoff');
      }
      if (elapsed >= duration) finish();
      else frame = requestAnimationFrame(draw);
    };

    timer = setTimeout(finish, Math.max(0, duration - (performance.now() - start)));
    if (!enabled() || document.hidden) finish();
    else frame = requestAnimationFrame(draw);
  }

  /* ------------------------------------------------- hero exit & entrances */

  // One controller per Shopify section; no duplicate listeners on editor reloads.
  const controllers = new Map();
  function mount(section) {
    if (controllers.has(section)) return;
    const abort = new AbortController();
    const { signal } = abort;
    const hero = section.querySelector('eden-hero');
    let front = [], raf = 0, lastProgress = -1, lastBlur = -1;
    const resetHero = () => {
      if (!hero) return;
      hero.removeAttribute('data-eden-exit');
      hero.style.removeProperty('transform'); hero.style.removeProperty('filter'); hero.style.removeProperty('opacity');
      front.forEach((el) => el.classList.remove('eden-motion-front')); front = []; lastProgress = -1; lastBlur = -1;
    };
    const drawHero = () => {
      raf = 0;
      if (!hero) return;
      const mobile = innerWidth < 750;
      if (!enabled() || !option('hero-exit', 1) || (mobile && !option('hero-exit-mobile', 1))) { resetHero(); return; }
      if (!front.length) {
        let next = section.nextElementSibling;
        while (next) { front.push(next); next.classList.add('eden-motion-front'); next = next.nextElementSibling; }
      }
      const rect = section.getBoundingClientRect();
      const distance = Math.max(0, -rect.top);
      const progress = Math.min(1, distance / Math.max(1, hero.offsetHeight * 0.8));
      if (Math.abs(progress - lastProgress) < 0.0005) return;
      lastProgress = progress;
      // Promote only while the hero is mid-dissolve; a permanent layer wastes phone memory.
      hero.setAttribute('data-eden-exit', progress > 0 && progress < 1 ? 'active' : '');

      const ease = progress * progress * (3 - 2 * progress);
      hero.style.transform = `translate3d(0,${Math.min(distance, hero.offsetHeight) * option('hero-drift', 35) / 100}px,0) scale(${1 - ease * option('hero-recede', 5) / 100})`;
      hero.style.opacity = String(1 - ease);

      /**
       * `filter: blur()` repaints the whole hero, which phones cannot do every frame.
       * Quantising it to whole pixels on mobile cuts those repaints by roughly ten times
       * while the transform and opacity keep moving smoothly on the compositor.
       */
      const ceiling = option('hero-blur', 12) * (mobile ? option('hero-blur-mobile', 55) / 100 : 1);
      const step = mobile ? 1 : 0.5;
      const blur = Math.round(Math.max(0, (progress - 0.15) / 0.85) * ceiling / step) * step;
      if (blur !== lastBlur) {
        lastBlur = blur;
        hero.style.filter = blur > 0 ? `blur(${blur}px)` : 'none';
      }
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(drawHero); };
    if (hero && document.body.classList.contains('eden-page-index')) {
      // Capture phase: scroll events do not bubble, but they do propagate down, so this
      // catches both `document` (mobile) and `.page-wrapper` (desktop squeeze layout).
      document.addEventListener('scroll', schedule, { passive: true, capture: true, signal });
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      squeeze.addEventListener('change', schedule, { signal });
      reduced.addEventListener('change', schedule, { signal });
      document.addEventListener('eden:intro:done', schedule, { signal });
      drawHero();
    }

    const pending = new Set(), animations = new Map();
    const clear = (item) => { item.removeAttribute('data-eden-entrance'); item.style.removeProperty('--eden-entrance-opacity'); item.style.removeProperty('--eden-entrance-distance'); };
    const stopReveals = () => { observer?.disconnect(); pending.forEach(clear); pending.clear(); animations.forEach((a) => a.cancel()); animations.clear(); };
    let observer;
    if (enabled() && option('reveal', 1) && 'IntersectionObserver' in window && typeof Element.prototype.animate === 'function') {
      const cinema = !!option('cinematic-reveal', 1);
      const duration = cinema ? option('cinematic-duration', 1100) : option('reveal-duration', 850);
      const distance = cinema ? option('cinematic-distance', 42) : option('reveal-distance', 24);
      const opacity = cinema ? 0 : option('reveal-opacity', 0.35);
      const mobile = innerWidth < 750;
      // Phones get the movement and the fade, but not the per-frame blur repaint.
      const blur = cinema && !mobile ? option('cinematic-blur', 3) : 0;
      observer = new IntersectionObserver((entries) => {
        let order = 0;
        entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top).forEach((entry) => {
          const item = entry.target; observer.unobserve(item); pending.delete(item); clear(item);
          if (!enabled()) return;
          const animation = item.animate([
            { opacity, translate: `0 ${distance}px`, scale: cinema ? '1.015' : '1', filter: `blur(${blur}px)` },
            { opacity: 1, translate: '0 0', scale: '1', filter: 'blur(0px)' },
          ], { duration, delay: cinema ? Math.min(order++ * option('cinematic-stagger', 80), 240) : 0, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'backwards' });
          animations.set(item, animation);
          animation.finished.catch(() => {}).finally(() => animations.delete(item));
        });
      }, {
        // The observer must watch the same box that scrolls, or nothing ever intersects.
        root: scroller(),
        threshold: 0,
        rootMargin: '0px 0px -40px 0px',
      });
      const selectors = '[data-eden-reveals] > *, .eden-section-head, .eden-title-row, featured-product-information .product-information__media, featured-product-information .product-details, .eden-archive-row, .section-resource-list .resource-list, .collection-links__container';
      const items = Array.from(section.querySelectorAll(selectors));
      items.filter((item) => !items.some((parent) => parent !== item && parent.contains(item))).forEach((item) => {
        if (item.closest('eden-hero, slideshow-slide') || (mobile && item.closest('.eden-cards'))) return;
        if (item.getBoundingClientRect().top < innerHeight) return;
        pending.add(item); item.setAttribute('data-eden-entrance', 'pending');
        item.style.setProperty('--eden-entrance-opacity', opacity);
        item.style.setProperty('--eden-entrance-distance', `${distance}px`); observer.observe(item);
      });
      reduced.addEventListener('change', () => { if (reduced.matches) stopReveals(); }, { signal });
      section.addEventListener('focusin', (event) => {
        pending.forEach((item) => { if (item.contains(event.target)) { observer.unobserve(item); pending.delete(item); clear(item); } });
      }, { signal });
      window.addEventListener('pageshow', (event) => { if (event.persisted) stopReveals(); }, { signal });
    }
    controllers.set(section, () => { abort.abort(); cancelAnimationFrame(raf); resetHero(); stopReveals(); });
  }
  document.querySelectorAll('main > .shopify-section').forEach(mount);
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => { controllers.get(event.target)?.(); controllers.delete(event.target); });
})();
